import { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { StatusBadge } from '@/components/common';
import {
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Menu,
  FileText,
  ClipboardCheck,
  Store,
  X,
  Home,
  Edit2,
  Eye,
  MoreVertical
} from 'lucide-react';
import {
  Box,
  Menu as MuiMenu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import { Spinner } from '@/components/common';
import { useMyTransfers, useDeleteTransfer } from '@/hooks/useTransfer';
import { useSellerTransferOffers, useUpdateOfferStage } from '@/hooks/useOffer';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import dayjs from '@/lib/dayjs';
import CreateTransferPage from '../CreateTransferPage';

export default function SellerManageTransfersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { addToast } = useUIStore();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const getActiveTabFromPath = (pathname: string, searchTab?: string | null) => {
    if (pathname.includes('/tin-dang')) return 'postings';
    if (pathname.includes('/dang-tin')) return 'create';
    if (pathname.includes('/sua')) return 'edit';
    if (pathname.includes('/de-nghi')) return 'overview';
    if (searchTab) return searchTab;
    return 'overview';
  };

  const activeTab = getActiveTabFromPath(location.pathname, searchParams.get('tab'));

  const setActiveTab = (tab: string) => {
    const pathToTab: Record<string, string> = {
      overview: '/sang-nhuong/quan-ly/de-nghi',
      postings: '/sang-nhuong/quan-ly/tin-dang',
      create: '/sang-nhuong/quan-ly/dang-tin',
      edit: '/sang-nhuong/quan-ly/sua',
    };
    navigate(pathToTab[tab] || '/sang-nhuong/quan-ly/de-nghi');
  };
  const [editingId, setEditingId] = useState<string | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionTransferId, setActionTransferId] = useState<string | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setActionTransferId(id);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActionTransferId(null);
  };

  // Queries
  const { data: myTransfersData, isLoading: loadingTransfers, refetch: refetchTransfers } = useMyTransfers();
  const myTransfers = Array.isArray(myTransfersData) ? myTransfersData : (myTransfersData as any)?.data || [];

  const { data: offersData, isLoading: loadingOffers, refetch: refetchOffers } = useSellerTransferOffers(user?.id || '', {
    page: 1,
    limit: 100,
    orderBy: 'createdAt',
    orderDirection: 'DESC',
  });
  const offers = Array.isArray(offersData) ? offersData : (offersData as any)?.data || [];

  // Mutations
  const { mutate: deleteTransfer, isPending: deletingTransferId } = useDeleteTransfer();
  const { mutate: updateOfferStage } = useUpdateOfferStage();

  const totalPostingsCount = myTransfers.length;
  const pendingOffersCount = offers.filter((o: any) => {
    const st = String(o.status || o.approvalStatus || '').toLowerCase();
    return st === 'pending' || st === 'screening' || !st;
  }).length;
  const acceptedOffersCount = offers.filter((o: any) => {
    const st = String(o.status || o.approvalStatus || '').toLowerCase();
    return st === 'accepted' || st === 'approved' || st === 'offer';
  }).length;

  const realOffers = offers.map((offer: any) => {
    const associatedTransfer = myTransfers.find((t: any) => t.id === offer.entityId);
    return {
      ...offer,
      transferTitle: associatedTransfer?.title || offer.content?.title || 'Tin sang nhượng',
    };
  });

  const handleDeleteTransfer = (id: string) => {
    if (confirm('Bạn có chắc chắn muốn xóa tin sang nhượng này?')) {
      deleteTransfer(id, {
        onSuccess: () => refetchTransfers()
      });
    }
  };

  const handleOfferStageChange = (id: string, stage: 'CONTACTED' | 'INTERVIEW' | 'PROBATION' | 'HIRED' | 'REJECTED') => {
    updateOfferStage({ id, stage }, {
      onSuccess: () => refetchOffers()
    });
  };

  const renderStatusBadge = (status: string, label: string) => {
    return <StatusBadge status={status} label={label} />;
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: '#fafafc', height: '100vh', width: '100vw', overflow: 'hidden', position: 'relative', fontFamily: 'sans-serif', color: '#1e293b' }}>
      
      {/* LEFT SIDEBAR MENU */}
      <Box
        component="aside"
        sx={{
          width: isSidebarCollapsed ? '80px' : '256px',
          bgcolor: '#f97316',
          backgroundImage: 'linear-gradient(to bottom, #f97316, #f97316)',
          color: 'white',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 30,
          boxShadow: '4px 0 10px rgba(0,0,0,0.05)',
          height: '100vh',
          flexShrink: 0
        }}
      >
        <div>
          <div className="p-6 border-b border-white/10 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shrink-0 shadow-inner">
              <Store size={20} className="text-white" />
            </div>
            {!isSidebarCollapsed && (
              <div className="animate-fade-in">
                <h2 className=" text-lg leading-tight tracking-wider">FNB HUB</h2>
                <p className="text-[10px] text-white/70 font-semibold uppercase tracking-widest">Seller Portal</p>
              </div>
            )}
          </div>

          <nav className="p-4 space-y-1">
            <button
              onClick={() => {
                setActiveTab('overview');
                setIsMobileSidebarOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all duration-200 text-left",
                activeTab === 'overview'
                  ? "bg-white text-[#f97316] shadow-md shadow-orange-950/10 scale-[1.02]"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              )}
            >
              <ClipboardCheck size={18} className="shrink-0" />
              {!isSidebarCollapsed && <span>Quản lý đề nghị mua</span>}
            </button>

            <button
              onClick={() => {
                setActiveTab('postings');
                setIsMobileSidebarOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all duration-200 text-left",
                activeTab === 'postings'
                  ? "bg-white text-[#f97316] shadow-md shadow-orange-950/10 scale-[1.02]"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              )}
            >
              <FileText size={18} className="shrink-0" />
              {!isSidebarCollapsed && <span>Quản lý tin sang nhượng</span>}
            </button>

            <button
              onClick={() => {
                setActiveTab('create');
                setIsMobileSidebarOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all duration-200 text-left",
                activeTab === 'create'
                  ? "bg-white text-[#f97316] shadow-md shadow-orange-950/10 scale-[1.02]"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              )}
            >
              <Plus size={18} className="shrink-0" />
              {!isSidebarCollapsed && <span>Đăng tin sang nhượng</span>}
            </button>
          </nav>
        </div>

        <div className="p-4 border-t border-white/10 space-y-2.5">
          <Link
            to="/"
            className={cn(
              "w-full h-11 flex items-center justify-center rounded-2xl text-xs font-bold text-white bg-white/10 hover:bg-white/20 border border-white/15 transition-all gap-2 shadow-inner",
              isSidebarCollapsed ? "px-0" : "px-4"
            )}
          >
            <Home size={16} className="shrink-0" />
            {!isSidebarCollapsed && <span>Quay lại trang chủ</span>}
          </Link>

          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className={cn(
              "w-full h-11 flex items-center justify-center rounded-2xl text-xs font-bold text-white bg-white/10 hover:bg-white/20 border border-white/15 transition-all gap-2 shadow-inner",
              isSidebarCollapsed ? "px-0" : "px-4"
            )}
          >
            {isSidebarCollapsed ? <ChevronRight size={16} className="shrink-0" /> : <ChevronLeft size={16} className="shrink-0" />}
            {!isSidebarCollapsed && <span>Thu gọn</span>}
          </button>
        </div>
      </Box>

      {/* MOBILE SIDEBAR */}
      {isMobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 md:hidden flex animate-fade-in" onClick={() => setIsMobileSidebarOpen(false)}>
          <aside className="w-64 bg-gradient-to-b from-[#f97316] to-[#f97316] text-white flex flex-col justify-between h-full p-4 animate-in slide-in-from-left duration-200" onClick={(e) => e.stopPropagation()}>
            <div>
              <div className="flex items-center justify-between pb-6 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white">
                    <Store size={16} />
                  </div>
                  <div>
                    <h2 className=" text-sm tracking-wider">FNB HUB</h2>
                    <p className="text-[8px] text-white/60">Seller Portal</p>
                  </div>
                </div>
                <button onClick={() => setIsMobileSidebarOpen(false)} className="text-white hover:bg-white/15 p-1 rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <nav className="mt-4 space-y-1">
                <button
                  onClick={() => {
                    setActiveTab('overview');
                    setIsMobileSidebarOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left",
                    activeTab === 'overview' ? "bg-white text-[#f97316]" : "text-white/80 hover:bg-white/10"
                  )}
                >
                  <ClipboardCheck size={16} />
                  <span>Quản lý đề nghị mua</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('postings');
                    setIsMobileSidebarOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left",
                    activeTab === 'postings' ? "bg-white text-[#f97316]" : "text-white/80 hover:bg-white/10"
                  )}
                >
                  <FileText size={16} />
                  <span>Quản lý tin sang nhượng</span>
                </button>
                
                <button
                  onClick={() => {
                    setActiveTab('create');
                    setIsMobileSidebarOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left",
                    activeTab === 'create' ? "bg-white text-[#f97316]" : "text-white/80 hover:bg-white/10"
                  )}
                >
                  <Plus size={16} />
                  <span>Đăng tin mới</span>
                </button>

                <Link
                  to="/"
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold text-white/85 hover:bg-white/10 transition-all mt-4 border border-white/15 bg-white/5"
                >
                  <ChevronLeft size={16} />
                  <span>Quay lại Trang chủ</span>
                </Link>
              </nav>
            </div>
          </aside>
        </div>
      )}

      {/* MAIN CONTENT */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100vh', overflow: 'hidden' }}>
        
        {/* TOP BAR */}
        <Box component="header" sx={{ height: '80px', bgcolor: 'white', borderBottom: '1px solid #f1f3f5', px: 3, display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, zIndex: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="md:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-50 hover:text-[#f97316] transition-colors"
            >
              <Menu size={22} />
            </button>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 cursor-pointer hover:text-[#f97316] transition-colors text-sm" onClick={() => setActiveTab('overview')}>
                Sang nhượng
              </span>
              <span className="text-gray-300">/</span>
              <span className=" text-gray-800 capitalize text-sm">
                {activeTab === 'overview' && 'Đề nghị mua'}
                {activeTab === 'postings' && 'Quản lý tin đăng'}
                {activeTab === 'create' && 'Đăng tin mới'}
                {activeTab === 'edit' && 'Sửa tin đăng'}
              </span>
            </div>
          </div>
        </Box>

        <Box component="main" sx={{ p: { xs: 3, md: 4 }, flex: 1, overflowY: 'auto', bgcolor: '#fafafc' }}>
          
          {/* TAB OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-8 animate-fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h1 className="font-display font-bold text-2xl text-gray-900 leading-tight">Quản lý đề nghị mua</h1>
                  <p className="text-xs text-gray-500 font-medium mt-1">Quản lý các đề nghị thương lượng cho cửa hàng của bạn.</p>
                </div>
                <button
                  onClick={() => setActiveTab('create')}
                  className="bg-[#f97316] hover:bg-[#e04700] text-white text-sm px-6 py-2.5 rounded-2xl flex items-center gap-2 shadow-lg shadow-orange-100 transition-all hover:scale-102 w-max"
                >
                  <Plus size={16} /> Đăng tin mới
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[
                  { title: 'Đề nghị đang chờ', value: pendingOffersCount, icon: <AlertCircle size={24} className="text-orange-500" />, bg: 'bg-orange-50' },
                  { title: 'Đề nghị đã chấp nhận', value: acceptedOffersCount, icon: <CheckCircle2 size={24} className="text-green-500" />, bg: 'bg-green-50' },
                  { title: 'Tổng tin đăng', value: totalPostingsCount, icon: <Store size={24} className="text-blue-500" />, bg: 'bg-blue-50' },
                ].map((stat, idx) => (
                  <div key={idx} className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex items-center gap-5 hover:shadow-md transition-shadow">
                    <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center shrink-0", stat.bg)}>
                      {stat.icon}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-500 mb-1">{stat.title}</p>
                      <h3 className="text-3xl font-display font-black text-gray-900 leading-none">{stat.value}</h3>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-bold text-lg text-gray-900">Danh sách đề nghị</h3>
                </div>
                
                <div className="p-0 overflow-x-auto">
                  {loadingOffers ? (
                    <div className="flex justify-center p-12"><Spinner /></div>
                  ) : realOffers.length === 0 ? (
                    <div className="text-center p-12">
                      <p className="text-gray-500">Chưa có đề nghị nào.</p>
                    </div>
                  ) : (
                    <table className="w-full text-left border-collapse text-sm">
                      <thead>
                        <tr className="bg-gray-50/50">
                          <th className="p-4 font-bold text-gray-500 border-b">Ứng viên / Khách hàng</th>
                          <th className="p-4 font-bold text-gray-500 border-b">Tin sang nhượng</th>
                          <th className="p-4 font-bold text-gray-500 border-b">Lời nhắn</th>
                          <th className="p-4 font-bold text-gray-500 border-b">Trạng thái</th>
                          <th className="p-4 font-bold text-gray-500 border-b">Ngày gửi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {realOffers.map((offer: any) => (
                          <tr key={offer.id} className="border-b hover:bg-gray-50/50 transition-colors">
                            <td className="p-4 font-medium text-gray-900">{offer.sender?.fullName || offer.sender?.username || 'Khách hàng'}</td>
                            <td className="p-4 text-gray-600 truncate max-w-[200px]">{offer.transferTitle}</td>
                            <td className="p-4 text-gray-600 truncate max-w-[200px]">{offer.message}</td>
                            <td className="p-4">
                              {renderStatusBadge(offer.status, ['accepted', 'approved', 'offer'].includes(String(offer.status).toLowerCase()) ? 'Đã duyệt' : String(offer.status).toLowerCase() === 'rejected' ? 'Từ chối' : 'Chờ duyệt')}
                            </td>
                            <td className="p-4 text-gray-500">{dayjs(offer.createdAt).format('DD/MM/YYYY')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB POSTINGS */}
          {activeTab === 'postings' && (
            <div className="space-y-8 animate-fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h1 className="font-display font-bold text-2xl text-gray-900 leading-tight">Quản lý tin đăng</h1>
                  <p className="text-xs text-gray-500 font-medium mt-1">Danh sách các cửa hàng bạn đang rao sang nhượng.</p>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-0 overflow-x-auto">
                  {loadingTransfers ? (
                    <div className="flex justify-center p-12"><Spinner /></div>
                  ) : myTransfers.length === 0 ? (
                    <div className="text-center p-12">
                      <p className="text-gray-500">Bạn chưa có tin đăng nào.</p>
                      <Button className="mt-4" variant="brand" onClick={() => setActiveTab('create')}>Tạo tin ngay</Button>
                    </div>
                  ) : (
                    <table className="w-full text-left border-collapse text-sm">
                      <thead>
                        <tr className="bg-gray-50/50">
                          <th className="p-4 font-bold text-gray-500 border-b">Tiêu đề tin</th>
                          <th className="p-4 font-bold text-gray-500 border-b">Loại hình</th>
                          <th className="p-4 font-bold text-gray-500 border-b">Trạng thái</th>
                          <th className="p-4 font-bold text-gray-500 border-b">Thao tác</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myTransfers.map((post: any) => (
                          <tr key={post.id} className="border-b hover:bg-gray-50/50 transition-colors">
                            <td className="p-4 font-medium text-gray-900">
                              <Link to={`/sang-nhuong/${post.id}`} className="hover:text-orange-500 line-clamp-1">{post.title}</Link>
                            </td>
                            <td className="p-4 text-gray-600">{post.businessTypeName || 'Nhà hàng'}</td>
                            <td className="p-4">
                              <StatusBadge
                                status={post.approvalStatus || 'pending'}
                                label={post.approvalStatus === 'approved' ? 'Đã duyệt' : 'Chờ duyệt'}
                              />
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <Link
                                  to={`/sang-nhuong/${post.id}`}
                                  className="w-8 h-8 rounded-full border flex items-center justify-center text-gray-500 hover:text-orange-500 hover:border-orange-500 hover:bg-orange-50 transition-colors"
                                  title="Xem chi tiết"
                                >
                                  <Eye size={16} />
                                </Link>
                                <button
                                  onClick={(e) => handleOpenMenu(e, post.id)}
                                  className="w-8 h-8 rounded-full border flex items-center justify-center text-gray-500 hover:text-orange-500 hover:border-orange-500 hover:bg-orange-50 transition-colors"
                                >
                                  <MoreVertical size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <MuiMenu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                sx={{
                  '& .MuiPaper-root': {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.08))',
                    mt: 1,
                    borderRadius: '16px',
                    border: '1px solid #f1f3f5',
                    minWidth: '160px'
                  }
                }}
              >
                <MenuItem onClick={() => { setEditingId(actionTransferId); setActiveTab('edit'); handleCloseMenu(); }} sx={{ fontSize: '13px', fontWeight: 'bold', color: '#64748b', py: 1.5, '&:hover': { color: '#f97316', backgroundColor: '#fff2eb' } }}>
                  <ListItemIcon>
                    <Edit2 size={16} className="text-inherit" />
                  </ListItemIcon>
                  Sửa tin
                </MenuItem>
                <MenuItem onClick={() => { if(actionTransferId) handleDeleteTransfer(actionTransferId); handleCloseMenu(); }} sx={{ fontSize: '13px', fontWeight: 'bold', color: '#ef4444', py: 1.5, '&:hover': { color: '#ef4444', backgroundColor: '#fef2f2' } }}>
                  <ListItemIcon>
                    <Trash2 size={16} className="text-[#ef4444]" />
                  </ListItemIcon>
                  Xóa tin
                </MenuItem>
              </MuiMenu>
            </div>
          )}

          {/* TAB CREATE/EDIT */}
          {(activeTab === 'create' || activeTab === 'edit') && (
            <div className="bg-white rounded-3xl p-6 md:p-8 border border-gray-100 shadow-sm animate-fade-in relative min-h-[600px]">
              <CreateTransferPage
                transferId={activeTab === 'edit' && editingId ? editingId : undefined}
                onSuccess={() => {
                  setActiveTab('postings');
                  refetchTransfers();
                  setEditingId(null);
                  addToast('success', activeTab === 'create' ? 'Đăng tin thành công' : 'Cập nhật tin thành công');
                }}
                onCancel={() => {
                  setActiveTab('postings');
                  setEditingId(null);
                }}
              />
            </div>
          )}

        </Box>
      </Box>
    </Box>
  );
}
